import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, ArrowLeft, Loader2, MailCheck, Copy, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [lastActivity, setLastActivity] = useState(null);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      setEmailSent(Boolean(data?.email_sent));
      setResetLink(data?.reset_link || "");
      setLastActivity(data?.last_activity || null);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${resetLink}`);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="hero-theme min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate("/login")}
          className="mb-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
          data-testid="forgot-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 text-amber-400 flex items-center justify-center ring-4 ring-slate-900/5">
            <Shield className="w-6 h-6" strokeWidth={2.6} />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">My Money Hero</p>
            <h1 className="text-2xl font-black text-slate-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Forgot password
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6">
          {!sent ? (
            <form onSubmit={onSubmit} className="space-y-4">
              <p className="text-sm font-medium text-slate-600 leading-relaxed">
                Enter the email tied to your hero account. We'll send a one-time link valid for 1 hour.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Email
                </Label>
                <Input
                  id="email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="hero@example.com" className="h-11 rounded-xl"
                  data-testid="forgot-email-input"
                />
              </div>

              {err && (
                <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <Button
                type="submit" disabled={busy}
                className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black tracking-wide"
                data-testid="forgot-submit-button"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-sent-state">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
                  <MailCheck className="w-6 h-6" strokeWidth={2.4} />
                </div>
                <div>
                  <p className="text-base font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {emailSent ? "Email on its way" : "Check your inbox"}
                  </p>
                  <p className="text-sm font-medium text-slate-500">
                    {emailSent
                      ? <>We just emailed <span className="font-semibold text-slate-900">{email}</span> a reset link.</>
                      : <>If <span className="font-semibold text-slate-900">{email}</span> is registered, a reset link is on its way.</>}
                  </p>
                </div>
              </div>

              {lastActivity && (
                <div
                  className="rounded-2xl bg-blue-50 border-2 border-blue-100 p-3 flex items-start gap-2"
                  data-testid="forgot-last-activity"
                >
                  <Eye className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" strokeWidth={2.6} />
                  <div className="text-xs text-blue-900 leading-relaxed">
                    <span className="font-bold uppercase tracking-wider text-[10px] text-blue-700">Recent activity</span>
                    <p className="mt-0.5 font-semibold">
                      Last sign-in: {lastActivity.device}
                      {lastActivity.ip_hint && <> · IP {lastActivity.ip_hint}</>}
                      {lastActivity.when && <> · {lastActivity.when}</>}
                    </p>
                    <p className="mt-1 text-[11px] text-blue-700/80">
                      Don't recognise it? Reset your password and we'll sign every device out.
                    </p>
                  </div>
                </div>
              )}

              {resetLink && (
                <div className="rounded-2xl bg-slate-50 border-2 border-dashed border-slate-200 p-3" data-testid="forgot-dev-link">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1.5">
                    Preview-mode link
                  </p>
                  <div className="flex items-center gap-2">
                    <Link
                      to={resetLink}
                      className="flex-1 text-xs font-mono text-slate-700 underline truncate"
                      data-testid="forgot-dev-link-anchor"
                    >
                      {`${window.location.origin}${resetLink}`}
                    </Link>
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={copyLink}
                      className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:bg-slate-200"
                      data-testid="forgot-dev-link-copy"
                      aria-label="Copy reset link"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    Email transport isn't reachable, so we surface the link for you.
                  </p>
                </div>
              )}

              <Button
                onClick={() => navigate("/login")}
                className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-bold tracking-wide"
                data-testid="forgot-back-to-login-button"
              >
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
